-- | Errors have a couple of different contexts that we should pay attention to.
-- | One such instance is when we are developing on the application we want
-- | debugging information in the console with a relative high density of
-- | information about what's going on. However, this doesn't work well once we
-- | are running in production where we want only very important sources of
-- | information to show up and some sensitive information to be scrubbed if it
-- | gets sent over the web to Sentry so we can get alerted that an error
-- | occured.
-- |
-- | To solve these problems, we have this module to manage how the rest of the
-- | application interacts with errors.

module Error where

-- TODO: Nice human readable formatter
-- TODO: Send Sentry data when enabled by the environment
-- TODO: Provide an easy way to only allow data that we know is good to sentry
-- TODO: Port debug message builder

import Prelude

import Affjax as AX
import Concur.Core (Widget)
import Concur.React (HTML)
import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Monad.Except.Trans (ExceptT(..), runExceptT)
import Data.Argonaut (Json, JsonDecodeError, printJsonDecodeError, stringify, (.:))
import Data.Argonaut as J
import Data.Argonaut.Decode.Decoders (decodeJObject)
import Data.Array ((!!))
import Data.Either (Either(..), hush)
import Data.Maybe (Maybe(..))
import Data.Tuple (Tuple(..))
import Effect (Effect)
import Effect.Class (liftEffect)
import Foreign.Object (Object)
import Foreign.Object as O
import GraphQL.Type (TypedID, innerId)
import Internal.ConcurUtil (loopInfinitely)
import Translate (lookupTranslation)

foreign import sentryFlag :: Boolean
foreign import initSentry :: Effect Unit
foreign import reloadPage :: Effect Unit
foreign import captureError :: SentryError -> Effect String

foreign import prettyPrintJson :: Json -> String

type SentryError =
    { message :: String
    , contexts :: Object Json
    }

data ErrorWrapper
    = AffJaxError AX.Error
    | DecodeError JsonDecodeError
    | QueryError (Array Json)
    | StringError String
    | WithContext ErrorContext ErrorWrapper

instance showErrorWrapper :: Show ErrorWrapper where
    show (AffJaxError err) = AX.printError err
    show (DecodeError err) = printJsonDecodeError err
    show (QueryError _) = "backend rejected query"
    show (StringError err) = err
    show (WithContext ctx err) = show ctx <> ": " <> show err

-- | Get a user-displayable message for the given ErrorWrapper
userError :: ErrorWrapper -> String
userError (QueryError jsons) =
    case msgM of
        Just msg -> msg
        Nothing -> lookupTranslation "generic-error"
  where
    msgM = do
       json <- jsons !! 0
       obj <- hush $ decodeJObject json
       hush (obj .: "message")
userError (WithContext _ err) = userError err
userError _ = lookupTranslation "generic-error"

renderError_ :: forall a. ErrorWrapper -> Widget HTML a
renderError_ (WithContext (InQuery { query, vars }) err) =
    D.div
        [P.className "error-context-wrapper pl-4"]
        [ D.p' [ D.text $ "While executing the following query: " ]
        , D.pre' [ D.text query ]
        , D.details'
            [ D.summary' [ D.text $ "Click to show query variables" ]
            , D.pre' [ D.text $ prettyPrintJson vars ]
            ]
        , renderError_ err ]
renderError_ (WithContext (LoadingJson desc json) err) =
    D.div
        [ P.className "error-context-wrapper pl-4" ]
        [ D.details'
            [ D.summary' [ D.text $ "While decoding JSON data" <> if desc == "" then "" else " for " <> desc ]
            , D.pre' [ D.text $ prettyPrintJson json ]
            ]
        , renderError_ err ]
renderError_ (WithContext ctx err) =
    D.div
        [ P.className "error-context-wrapper pl-4" ]
        [ D.text $ "In context: " <> show ctx
        , renderError_ err ]
renderError_ (QueryError msgs) = D.div
    [ P.className "error-context-error pl-4" ]
    [ D.text "The backend rejected the query with the following errors:"
    , D.ul' $ map (\msg -> D.li' [ D.pre' [ D.text $ prettyPrintJson msg ] ]) msgs
    ]
renderError_ err = D.div
    [ P.className "error-context-error pl-4" ]
    [ D.p' [ D.text $ "Error: " <> show err ] ]

toSentry :: ErrorWrapper -> SentryError
toSentry (WithContext ctx err) =
    if O.member key inner.contexts
        then inner
        else inner { message = contextPrefix ctx <> inner.message
                   , contexts = O.insert key sctx inner.contexts }
  where
    (Tuple key sctx) = encodeContext ctx
    inner = toSentry err
toSentry err@(QueryError msgs) = { message: show err, contexts: O.fromHomogeneous
    { backend_errors: J.encodeJson { errors: msgs } }}
toSentry err = { message: show err, contexts: O.empty }

data ErrorContext
    = InFormWidget String
    -- ^ Error occurred inside a form widget.
    | InModule String
    -- ^ Purescript module the error occurred in.
    | InQuery { query :: String, vars :: Json }
    -- ^ Error while running the given query.
    | InForm (TypedID "Form") String
    -- ^ Error while filling out form (type) with given ID and name
    | LoadingJson String Json
    -- ^ Use for attaching some JSON object that was being loaded at the time
    -- the error occurred, with a string description.
    | DuringAction String
    -- ^ General description of what was happening during the error.

-- | Encodes the error context to JSON.
-- |
-- | When giving data to sentry, contexts are uniquely identified by the key
-- | returned here. Duplicates will be overridden by more deeply nested
-- | `WithContext` objects.
encodeContext :: ErrorContext -> Tuple String Json
encodeContext (InFormWidget v) = Tuple "form-widget" $ J.encodeJson { widget: v }
encodeContext (InModule v) = Tuple "module" $ J.encodeJson { module: v }
encodeContext (InForm id name) = Tuple "form" $ J.encodeJson { id, name }
encodeContext (InQuery {query, vars}) = Tuple "query" $ J.encodeJson { query, vars: prettyPrintJson vars }
encodeContext (LoadingJson desc json) = Tuple ("load-json-" <> desc) $ J.encodeJson
    { description: desc, json: prettyPrintJson json }
encodeContext (DuringAction act) = Tuple "action" $ J.encodeJson { action: act }

-- | Used for contexts that modify the sentry error message.
contextPrefix :: ErrorContext -> String
contextPrefix (InFormWidget v) = "in form widget " <> v <> ": "
contextPrefix (InForm id name) = "form \"" <> name <> "\" (" <> innerId id <> "): "
contextPrefix (DuringAction act) = "while " <> act <> ": "
contextPrefix _ = ""

instance showCtx :: Show ErrorContext where
    show (InFormWidget widget) = "form widget \"" <> widget <> "\""
    show (InModule mod) = "purescript module \"" <> mod <> "\""
    show (InForm id name) = "form \"" <> name <> "\" (" <> innerId id <> ")"
    show (InQuery {query}) = "query \"" <> query <> "\""
    show (LoadingJson "" json) = "while loading json: \"" <> stringify json <> "\""
    show (LoadingJson desc json) = "while loading " <> desc <> " json: \"" <> stringify json <> "\""
    show (DuringAction desc) = "while " <> desc

wrapJsonError :: forall a. Either JsonDecodeError a -> Either ErrorWrapper a
wrapJsonError = onLeft DecodeError

-- | A version of `wrapJsonError which also includes the object which triggered
-- | the error.
wrapJsonError' :: forall a. Json -> Either JsonDecodeError a -> Either ErrorWrapper a
wrapJsonError' obj = onLeft (WithContext (LoadingJson "" obj) <<< DecodeError)


withError :: forall a e e'. (e -> e') -> Either e a -> Either e' a
withError f (Left err) = Left $ f err
withError _ (Right v) = Right v

withError' :: forall a m e e'. Monad m => (e -> e') -> ExceptT e m a -> ExceptT e' m a
withError' f ext = ExceptT do
    ret <- runExceptT ext
    pure $ withError f ret

tagContext :: forall a. ErrorContext -> Either ErrorWrapper a -> Either ErrorWrapper a
tagContext ctx = withError (WithContext ctx)

tagContext' :: forall a m. Monad m => ErrorContext -> ExceptT ErrorWrapper m a -> ExceptT ErrorWrapper m a
tagContext' ctx = withError' (WithContext ctx)


tagWidget :: forall a. String -> Either ErrorWrapper a -> Either ErrorWrapper a
tagWidget = tagContext <<< InFormWidget

tagModule :: forall a. String -> Either ErrorWrapper a -> Either ErrorWrapper a
tagModule = tagContext <<< InModule

tagAction :: forall a. String -> Either ErrorWrapper a -> Either ErrorWrapper a
tagAction = tagContext <<< DuringAction


-- | Handles a critical error which cannot be recovered from. Execution stops
-- | after calling this.
-- |
-- | This deals with showing the appropriate error page (something friendly in
-- | production, and lots of details in dev).
handleCritical :: forall a. ErrorWrapper -> Widget HTML a
handleCritical err = do
    errId <- liftEffect $ reportError err
    displayError errId $ if sentryFlag then D.div' [] else renderErrorDetails err

wrapCritical :: forall a. Either ErrorWrapper a -> Widget HTML a
wrapCritical (Left err) = handleCritical err
wrapCritical (Right ok) = pure ok


-- | Report an error to sentry.
reportError :: ErrorWrapper -> Effect String
reportError error = do
    let serror = toSentry error
    captureError serror


displayError :: forall a. String -> Widget HTML a -> Widget HTML a
displayError errId = displayError_ errId reloadButton

reloadButton :: forall a. Widget HTML a
reloadButton = do
    _ <- D.button
        [ P.onClick
        , P.className "btn btn-primary" ]
        [ D.text $ lookupTranslation "error-button-text" ]
    liftEffect $ reloadPage
    loopInfinitely $ D.div' []

displayError_ :: forall a. String -> Widget HTML a -> Widget HTML a -> Widget HTML a
displayError_ errId btn inner = D.div
    [ P.className "error-page" ]
    [ D.h1' [ D.text $ lookupTranslation "error-page-header" ]
    , D.p' [ D.text $ lookupTranslation "error-refcode-text" <> " " <> errId ]
    , D.p' [ D.text $ lookupTranslation "error-button-pre-text" ]
    , btn
    , inner
    ]

-- | For development environments with no sentry, renders detailed error info to HTML.
renderErrorDetails :: forall a. ErrorWrapper -> Widget HTML a
renderErrorDetails err = D.div'
    [ D.p' [ D.text "More information about this error:" ]
    , D.div
        [ P.className "error-details" ]
        [ renderError_ err ]
    ]

onLeft :: forall a b e. (e -> b) -> Either e a -> Either b a
onLeft fn = case _ of
    Left e -> Left $ fn e
    Right a -> Right a
